import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dog, X, Send, PawPrint } from "lucide-react";
import { api } from "@/lib/helpers";

const CHIPS = [
  "How do I adopt?",
  "Who's up for adoption?",
  "How does my donation help?",
  "Can I volunteer?",
];

const WELCOME = {
  role: "assistant",
  content: "Hi there! I'm Daisy, the sanctuary's chat pal. Ask me about adoptions, sponsoring an animal, volunteering, donating or our wish list — I know all the good stuff.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const sessionId = useRef(`${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    const history = messages.filter((m) => m !== WELCOME).slice(-8).map(({ role, content }) => ({ role, content }));
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg, history }),
      });
      if (!res.ok || !res.body) throw new Error(`Chat is napping (HTTP ${res.status}) — try again in a moment.`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.error) throw new Error(payload.error);
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
                return copy;
              });
            }
          } catch (e) { if (e instanceof SyntaxError) continue; throw e; }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `Whiskers crossed — something went sideways (${e.message}).` };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="chat-open-btn"
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setOpen(true)}
            className={`fixed bottom-6 left-4 sm:left-6 z-[70] items-center gap-2 rounded-full bg-pine text-sand px-5 py-4 font-bold shadow-2xl shadow-pine/40 hover:scale-105 transition-transform ${pastHero ? "flex" : "hidden sm:flex"}`}>
            <Dog className="h-5 w-5 text-gold-soft" />
            <span className="hidden sm:inline text-sm">Ask Daisy</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className="fixed bottom-0 left-0 right-0 z-[75] sm:bottom-6 sm:left-6 sm:right-auto sm:w-[400px] flex flex-col rounded-t-3xl sm:rounded-3xl bg-pine-deep text-sand shadow-2xl overflow-hidden border border-sand/10"
            style={{ height: "min(600px, 78vh)" }}
          >
            <div className="flex items-center justify-between border-b border-sand/10 bg-pine px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/20">
                  <PawPrint className="h-4.5 w-4.5 text-gold-soft" />
                </span>
                <div>
                  <p className="font-serif text-lg leading-tight">Daisy</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-sand/50">
                    {streaming ? "typing…" : "Sanctuary chat pal"}
                  </p>
                </div>
              </div>
              <button data-testid="chat-close" onClick={() => setOpen(false)} className="rounded-full bg-sand/10 p-2 hover:bg-sand/20">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-gold text-charcoal rounded-br-sm" : "bg-pine/70 text-sand/90 rounded-bl-sm"
                  }`} data-testid={m.role === "user" ? "chat-msg-user" : "chat-msg-assistant"}>
                    {m.content || <span className="inline-flex gap-1"><PawPrint className="h-3 w-3 animate-pulse-soft" /><PawPrint className="h-3 w-3 animate-pulse-soft" /><PawPrint className="h-3 w-3 animate-pulse-soft" /></span>}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {CHIPS.map((c) => (
                    <button key={c} data-testid={`chat-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                      onClick={() => send(c)}
                      className="rounded-full border border-gold/40 bg-gold/10 px-3.5 py-2 text-xs font-semibold text-gold-soft hover:bg-gold hover:text-charcoal transition-colors">
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-sand/10 bg-pine-deep p-3">
              <div className="flex items-center gap-2">
                <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about adoptions, donating…"
                  className="flex-1 rounded-full bg-pine/60 px-4 py-3 text-sm placeholder:text-sand/40 outline-none focus:ring-2 focus:ring-gold" />
                <button data-testid="chat-send-btn" onClick={() => send()} disabled={streaming || !input.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold text-charcoal transition-all hover:bg-gold-soft disabled:opacity-40">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
